import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, num, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useOpenMonths } from '../utils/months';
import PayslipView from '../components/PayslipView';
import { useDataTable, TableFilterRow, useRowPick, PickedChip } from '../components/DataTable';

// Add a one-off bonus / deduction to an employee for the payroll month, straight
// from the run — it posts a ledger entry the run already sums, so net pay updates.
const AdjustModal = ({ row, month, onClose, onSaved }) => {
  const [bonus, setBonus] = useState('');
  const [deduction, setDeduction] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const b = Number(bonus) || 0, d = Number(deduction) || 0;
  const newNet = Number(row.netPay || 0) + b - d;
  const save = async () => {
    if (b <= 0 && d <= 0) return toast.error('Enter a bonus or deduction amount');
    setBusy(true);
    try {
      const date = `${month}-15`;   // any day in the month — the run keys off the month
      if (b > 0) await api.post(`/employees/${row.employeeId}/ledger`, { date, kind: 'bonus', amount: b, note: note || 'Payroll adjustment' });
      if (d > 0) await api.post(`/employees/${row.employeeId}/ledger`, { date, kind: 'deduction', amount: d, note: note || 'Payroll adjustment' });
      toast.success('Adjustment added'); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Adjust — ${row.name}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy || (b <= 0 && d <= 0)}>{busy ? 'Saving…' : 'Add adjustment'}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>Add a one-off bonus or deduction for <strong>{row.name}</strong> · {month}. It updates net pay before you finalize.</p>
      <div className="row">
        <div className="field"><label>Bonus (+₹)</label><input type="number" min="0" step="0.01" value={bonus} onChange={(e) => setBonus(e.target.value)} placeholder="0" autoFocus /></div>
        <div className="field"><label>Deduction (−₹)</label><input type="number" min="0" step="0.01" value={deduction} onChange={(e) => setDeduction(e.target.value)} placeholder="0" /></div>
      </div>
      <div className="field"><label>Note</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" /></div>
      <p className="muted small">Net pay: <strong>{inr(row.netPay)}</strong>{(b > 0 || d > 0) ? <> → <strong>{inr(newNet)}</strong></> : null}</p>
    </Modal>
  );
};

// Uploaded docs (the employee's UPI QR) are served at <host>/uploads/… , outside /api.
const FILE_HOST = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
const fileSrc = (u) => (u ? (u.startsWith('http') ? u : `${FILE_HOST}${u}`) : null);
const isImg = (u) => /\.(png|jpe?g|webp|gif)$/i.test(u || '');

const PAY_METHODS = [['upi', 'UPI'], ['bank', 'Bank transfer'], ['cash', 'Cash'], ['other', 'Other']];

const copy = async (text, what) => {
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(String(text));
    else throw new Error('no clipboard');
    toast.success(`${what} copied`);
  } catch { toast.error(`Copy failed — ${what}: ${text}`); }
};

// A read-only detail line with a copy button (UPI id, account number, IFSC…).
const CopyRow = ({ label, value }) => (
  <div className="flex gap" style={{ alignItems: 'center', justifyContent: 'space-between', padding: '.25rem 0' }}>
    <span className="muted small">{label}</span>
    <span className="flex gap" style={{ alignItems: 'center' }}>
      <strong style={{ wordBreak: 'break-all' }}>{value}</strong>
      <button className="btn btn-outline btn-sm" onClick={() => copy(value, label)}>Copy</button>
    </span>
  </div>
);

// Pay one employee: choose how the money actually leaves (UPI deep link / QR, bank,
// cash), record the reference, then finalize — which writes the payslip, recovers
// loan installments and settles the month's ledger entries.
const PayModal = ({ row, month, onClose, onPaid }) => {
  const [method, setMethod] = useState(row.upiId ? 'upi' : 'bank');
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // RazorpayX is offered only where the server has credentials for it, so an
  // ERP without them shows exactly what it showed before.
  const [payoutCfg, setPayoutCfg] = useState(null);
  const [sending, setSending] = useState(false);
  const [payout, setPayout] = useState(null);
  useEffect(() => { api.get('/payouts/config').then(({ data }) => setPayoutCfg(data)).catch(() => setPayoutCfg({ enabled: false })); }, []);

  const owed = Number(row.remaining != null ? row.remaining : row.netPay || 0);
  // What is being handed over now. Held as typed so a part payment can be
  // entered digit by digit; it defaults to everything still owed.
  const [amountText, setAmountText] = useState(String(owed.toFixed(2)));
  const amount = Math.min(Number(amountText) || 0, owed);
  const partial = amount > 0 && amount < owed - 0.005;
  // Standard UPI intent link: opens GPay/PhonePe/Paytm with payee + amount prefilled.
  const upiLink = row.upiId
    ? `upi://pay?pa=${encodeURIComponent(row.upiId)}&pn=${encodeURIComponent(row.name)}&am=${amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Salary ${month}`)}`
    : null;
  const hasBank = row.bankAccount || row.ifsc || row.bankName;

  const pay = async () => {
    if (amount <= 0 && !window.confirm(`Nothing is being paid. Raise the payslip anyway?`)) return;
    setBusy(true); setError('');
    try {
      // A payslip that already exists takes a further payment; one that does not
      // is raised now, paying whatever is being handed over.
      if (row.payslipId) {
        await api.post(`/employees/payslips/${row.payslipId}/payments`, {
          amount, mode: method, reference: ref.trim() || undefined, note: note.trim() || undefined,
        });
      } else {
        const { data } = await api.post('/employees/payroll/finalize', {
          month, employeeId: row.employeeId, amountPaid: amount,
          paidVia: method, paidRef: ref.trim() || undefined, note: note.trim() || undefined,
        });
        if (!data.created) throw new Error(data.skipped?.[0]?.reason || 'Nothing was finalized');
      }
      toast.success(partial
        ? `${row.name} — ${inr(amount)} paid, ${inr(owed - amount)} still owed`
        : `${row.name} paid — ${inr(amount)}`);
      onPaid(null); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  // Actually move the money. The payslip is finalized first — it is the record
  // of what the month came to — and the transfer is raised against it, so the
  // UTR lands back on that same payslip when the bank confirms.
  const sendViaBank = async () => {
    if (!window.confirm(`Send ${inr(amount)} to ${row.name}'s bank account?\n\nThis moves real money and cannot be undone from here.`)) return;
    setSending(true); setError('');
    try {
      let payslipId = row.payslipId;
      if (!payslipId) {
        const { data } = await api.post('/employees/payroll/finalize', {
          month, employeeId: row.employeeId, amountPaid: 0, paidVia: 'bank', note: note.trim() || undefined,
        });
        payslipId = data.payslips?.[0]?.id;
        if (!payslipId) throw new Error(data.skipped?.[0]?.reason || 'Could not finalize the payslip');
      }
      const { data } = await api.post('/payouts/salary', { payslipId, narration: `Salary ${month}` });
      setPayout(data.payout);
      const st = data.payout?.status;
      if (st === 'processed') toast.success(`${row.name} paid — ${inr(amount)}`);
      else if (st === 'failed') toast.error(`Transfer failed: ${data.payout.failureReason || 'see the payout record'}`);
      else toast(`Transfer ${st} — the status will update when the bank confirms.`);
      onPaid(null);
    } catch (e) { setError(apiError(e)); } finally { setSending(false); }
  };

  const canSend = payoutCfg?.enabled && method === 'bank' && hasBank && !payout;

  return (
    <Modal title={`Pay — ${row.name}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        {canSend && <button className="btn btn-primary" onClick={sendViaBank} disabled={sending || busy}>
          {sending ? 'Sending…' : `Send ${inr(amount)} to bank`}</button>}
        <button className={`btn ${canSend ? 'btn-outline' : 'btn-primary'}`} onClick={pay} disabled={busy || sending}>
          {busy ? 'Recording…' : `${partial ? 'Record part payment' : 'Mark paid'} · ${inr(amount)}`}</button></>}>
      <div className="flex gap" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span className="muted small">
          {row.paidAmount > 0.005
            ? <>Still owed · {month} <span className="muted">({inr(row.paidAmount)} of {inr(row.netPay)} paid)</span></>
            : <>Net pay · {month}</>}
        </span>
        <span style={{ fontSize: '1.4rem', fontWeight: 700 }}>{inr(owed)}</span>
      </div>

      <div className="row">
        <div className="field"><label>Paying now (₹)</label>
          <input type="number" min="0" step="0.01" max={owed} value={amountText}
            onChange={(e) => setAmountText(e.target.value)} />
        </div>
        <div className="field" style={{ justifyContent: 'flex-end' }}>
          {partial && <p className="muted small" style={{ margin: 0 }}>{inr(owed - amount)} will still be owed.</p>}
        </div>
      </div>

      <div className="flex gap" style={{ margin: '.6rem 0' }}>
        {PAY_METHODS.map(([m, label]) => (
          <button key={m} className={`pill-btn ${method === m ? 'active' : ''}`} onClick={() => setMethod(m)}>{label}</button>
        ))}
      </div>

      {method === 'upi' && (row.upiId || row.upiQrDocUrl ? (
        <div className="card card-pad" style={{ marginBottom: '.6rem' }}>
          {row.upiId ? <CopyRow label="UPI ID" value={row.upiId} /> : <p className="muted small" style={{ marginTop: 0 }}>No UPI ID on file — scan the QR below.</p>}
          <div className="flex gap" style={{ margin: '.4rem 0' }}>
            {upiLink && <a className="btn btn-primary btn-sm" href={upiLink}>Open UPI app</a>}
            <button className="btn btn-outline btn-sm" onClick={() => copy(amount.toFixed(2), 'Amount')}>Copy amount</button>
          </div>
          {isImg(row.upiQrDocUrl) && (
            <a href={fileSrc(row.upiQrDocUrl)} target="_blank" rel="noreferrer" title="Open full size">
              <img src={fileSrc(row.upiQrDocUrl)} alt={`${row.name} UPI QR`} style={{ maxWidth: 180, border: '1px solid var(--line)', borderRadius: 8 }} />
            </a>
          )}
          <p className="muted small" style={{ marginBottom: 0 }}>The link opens your UPI app with the payee and amount filled in. On a desktop, scan the QR and enter {inr(amount)}.</p>
        </div>
      ) : (
        <p className="form-error">No UPI ID or QR on file for {row.name}. Add one on their <Link to={`/employees/${row.employeeId}`}>employee page</Link>, or pay another way.</p>
      ))}

      {method === 'bank' && (hasBank ? (
        <div className="card card-pad" style={{ marginBottom: '.6rem' }}>
          {row.bankName && <CopyRow label="Bank" value={row.bankName} />}
          {row.bankAccount && <CopyRow label="Account" value={row.bankAccount} />}
          {row.ifsc && <CopyRow label="IFSC" value={row.ifsc} />}
          {payoutCfg?.enabled && !payout && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              <strong>Send {inr(amount)} to bank</strong> transfers it now through RazorpayX
              {payoutCfg.mode === 'test' && <span className="badge badge-gray" style={{ marginLeft: 6 }}>test keys — no real money moves</span>}
              . <strong>Mark paid</strong> only records a transfer you made yourself.
            </p>
          )}
          {payout && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              Transfer <strong>{payout.status}</strong>
              {payout.utr ? <> · UTR {payout.utr}</> : null}
              {payout.failureReason ? <span className="neg"> · {payout.failureReason}</span> : null}
            </p>
          )}
        </div>
      ) : (
        <p className="form-error">No bank details on file for {row.name}. Add them on their <Link to={`/employees/${row.employeeId}`}>employee page</Link>.</p>
      ))}

      <div className="row">
        <div className="field"><label>Reference {method === 'upi' ? '(UPI txn id)' : method === 'bank' ? '(UTR)' : '(optional)'}</label>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Optional — kept on the payslip" /></div>
        <div className="field"><label>Note</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></div>
      </div>

      <p className="muted small">{row.payslipId ? 'This is recorded against the existing payslip.' : `Paying finalizes the ${month} payslip:`} it recovers loan installments, updates the off balance and locks the month's attendance for {row.name}.</p>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const Payroll = () => {
  const { can } = useAuth();
  const canFinalize = can('payroll:write') || can('employees:write');
  const canAdjust = can('employees:write');
  // Payroll may only be run for an OPEN accounting month — the picker offers exactly
  // those, newest first, and starts on the current one. A month finance has not
  // opened is simply not on the list (and the API refuses to finalize it either way).
  const { months, current: currentMonth, loading: monthsLoading } = useOpenMonths();
  const [month, setMonth] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewSlip, setViewSlip] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [payRow, setPayRow] = useState(null);
  const pick = useRowPick();   // hold a row while reading across the deductions

  useEffect(() => {
    if (monthsLoading) return;
    setMonth((m) => m || currentMonth || '');
    if (!currentMonth) setLoading(false);
  }, [monthsLoading, currentMonth]);

  const load = useCallback(() => {
    if (!month) return;
    setLoading(true);
    api.get(`/employees/payroll/run?month=${month}`).then(({ data }) => setRows(data.rows || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [month]);
  useEffect(load, [load]);

  const pending = rows.filter((r) => !r.finalized);
  // Bulk close-out: no payout method is recorded here — pay row-by-row (Pay) when
  // you want the UPI/bank reference kept on the payslip.
  const finalizeAll = async () => {
    if (!window.confirm(`Finalize & mark paid for ${pending.length} employee(s) — ${month}? This records payslips and recovers loan installments.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/employees/payroll/finalize', { month });
      toast.success(`Finalized ${data.created} payslip(s)${data.skipped?.length ? `, ${data.skipped.length} skipped` : ''}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // After a single payout, show the printable payslip and refresh the run.
  const onPaid = (row) => (slip) => {
    setViewSlip({ ...row, ...(slip || {}), status: 'paid', paidAt: slip?.paidAt || new Date().toISOString() });
    load();
  };

  const dtPay = useDataTable(rows, {
    searchKeys: ['name', 'code', 'branch'],
    columns: [
      { key: 'name', label: 'Employee', value: (r) => r.name, placeholder: 'Employee' },
      { key: 'branch', label: 'Branch', value: (r) => r.branch, type: 'select' },
      { key: 'state', label: 'Status', value: (r) => (r.finalized ? 'Paid' : 'Pending'), type: 'select' },
    ],
    pageSize: 200,
  });
  // The totals follow what is on screen: a branch filtered down should show that
  // branch's wage bill, not the company's.
  const sum = (k) => dtPay.rows.reduce((s, r) => s + Number(r[k] || 0), 0);
  // Days elapsed is the same for almost everyone, so it sits in the column
  // header; only joiners and leavers mid-month show their own count.
  const headN = rows.reduce((n, r) => Math.max(n, Number(r.daysElapsed) || 0), 0);
  const headMonthDays = rows[0]?.monthDays;
  const headDays = rows.length && headN < headMonthDays ? `${headN}/${headMonthDays}d` : '';

  return (
    <div>
      <div className="section-head">
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <label className="muted small">Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 170 }} disabled={!months.length}>
            {!months.length && <option value="">No open month</option>}
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <span className="muted small">{rows.length} employees · {pending.length} pending</span>
          <PickedChip pick={pick} />
        </div>
        {canFinalize && <button className="btn btn-primary" onClick={finalizeAll} disabled={busy || !pending.length}>{busy ? 'Working…' : `Finalize all pending (${pending.length})`}</button>}
      </div>
      <p className="muted small" style={{ marginTop: '-.4rem' }}>Net = fixed salary − excess-off cut − salary advance − items taken − loan installment − other deductions + bonus. Offs beyond the accrued balance are prorated at salary ÷ days in month.</p>

      {!loading && !month && (
        <div className="card card-pad empty">Every recent month is closed. Reopen one in Finance › Month close to run payroll for it.</div>
      )}
      {month && !loading && (
        <div className="stat-strip">
          <div className="strip-cell"><span className="k">Net payable</span><span className="v">{inr(sum('netPay'))}</span></div>
          <div className="strip-cell"><span className="k">Paid</span><span className="v">{inr(sum('paidAmount'))}</span>
            <span className="muted small">{dtPay.rows.filter((r) => r.finalized && Number(r.remaining) <= 0.005).length} of {dtPay.rows.length} settled</span></div>
          <div className="strip-cell"><span className="k">Remaining</span>
            <span className={`v ${sum('remaining') > 0.005 ? 'neg' : ''}`}>{inr(sum('remaining'))}</span>
            <span className="muted small">{dtPay.rows.filter((r) => Number(r.remaining) > 0.005).length} still owed</span></div>
        </div>
      )}
      {loading ? <div className="spinner" /> : month && (
        <div className="table-wrap">
          <table className="grid pay-grid">
            <thead><tr>
              <th className="freeze-col">Employee</th><th>Branch</th><th className="right">Salary</th><th className="right">As of today{headDays && <span className="muted small"> {headDays}</span>}</th><th className="right" title="Offs carried forward + this month's allowance">Comp offs</th><th className="right">Off</th><th className="right">Absent</th><th className="right">Off cut</th>
              <th className="right">Advance</th><th className="right">Items</th><th className="right">Loan</th><th className="right">Other</th>
              <th className="right">Net pay</th><th className="right">Remaining</th><th>Status</th><th></th>
            </tr>
            <TableFilterRow dt={dtPay} cells={['name', 'branch', null, null, null, null, null, null, null, null, null, null, null, null, 'state', null]} />
            </thead>
            <tbody>
              {dtPay.rows.map((r) => {
                const avail = Number(r.offBalanceBefore || 0) + Number(r.offsAccrued || 0);
                const other = Number(r.otherDeductions || 0) - Number(r.bonus || 0);
                return (
                  <tr key={r.employeeId} className={`${r.excessOffs > 0 ? 'row-danger' : ''}${pick.picked.has(r.employeeId) ? ' row-picked' : ''}`}>
                    <td {...pick.cellProps(r.employeeId)}><Link to={`/employees/${r.employeeId}`} className="link-strong" onClick={(e) => e.stopPropagation()}>{r.name}</Link>{r.left && <span className="badge badge-gray" style={{ marginLeft: 6 }} title={r.exitDate ? `Left on ${r.exitDate}` : "No longer employed"}>left</span>}</td>
                    <td className="muted">{r.branch || '—'}</td>
                    <td className="right">{inr(r.fixedSalary)}</td>
                    <td className="right" title={`${r.daysElapsed ?? r.monthDays} of ${r.monthDays} days`}>
                      {inr(r.earnedSalary ?? r.fixedSalary)}
                      {r.partialMonth && Number(r.daysElapsed) !== headN && <span className="muted small"> {r.daysElapsed}/{r.monthDays}d</span>}
                    </td>
                    <td className="right">{num(avail, 1)}</td>
                    <td className="right">{r.offsTaken || '—'}{r.excessOffs > 0 && <span className="neg small"> ({r.excessOffs} excess)</span>}</td>
                    <td className="right">{r.absentDays ? <span className="neg">{r.absentDays}</span> : '—'}{r.halfDays > 0 && <span className="muted small"> +{r.halfDays}½</span>}</td>
                    <td className="right">{Number(r.excessOffDeduction) ? `−${inr(r.excessOffDeduction)}` : '—'}</td>
                    <td className="right">{Number(r.salaryAdvance) ? `−${inr(r.salaryAdvance)}` : '—'}</td>
                    <td className="right">{Number(r.itemsTotal) ? `−${inr(r.itemsTotal)}` : '—'}</td>
                    <td className="right">{Number(r.loanInstallment) ? `−${inr(r.loanInstallment)}` : '—'}</td>
                    <td className="right">{other ? (other > 0 ? `−${inr(other)}` : `+${inr(-other)}`) : '—'}</td>
                    <td className="right"><strong>{inr(r.netPay)}</strong></td>
                    <td className="right">{Number(r.remaining) > 0.005
                      ? <strong className="neg">{inr(r.remaining)}</strong>
                      : <span className="muted">—</span>}</td>
                    <td>{!r.finalized
                      ? <span className="badge badge-gold">Draft</span>
                      : Number(r.remaining) > 0.005
                        ? <><span className="badge badge-blue">Part paid</span><span className="muted small"> · {inr(r.paidAmount)}</span></>
                        : <><span className="badge badge-green">Paid</span>{r.paidVia && <span className="muted small"> · {r.paidVia.toUpperCase()}</span>}</>}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setViewSlip(r)}>Payslip</button>{' '}
                      {canAdjust && !r.finalized && <button className="btn btn-outline btn-sm" onClick={() => setAdjust(r)}>Adjust</button>}{' '}
                      {canFinalize && (!r.finalized || Number(r.remaining) > 0.005)
                        && <button className="btn btn-primary btn-sm" onClick={() => setPayRow(r)} disabled={busy}>
                          {r.finalized ? 'Pay balance' : 'Pay'}</button>}
                    </td>
                  </tr>
                );
              })}
              {!dtPay.rows.length && <tr><td colSpan={16} className="empty">{dtPay.isFiltered ? 'No employees match.' : 'No active employees.'}</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="total-row">
                <td className="freeze-col"><strong>Total</strong></td><td></td>
                <td className="right">{inr(sum('fixedSalary'))}</td>
                <td className="right">{inr(dtPay.rows.reduce((t, r) => t + Number((r.earnedSalary ?? r.fixedSalary) || 0), 0))}</td><td></td>
                <td className="right">{sum('offsTaken')}</td>
                <td className="right">{sum('absentDays')}</td>
                <td className="right">−{inr(sum('excessOffDeduction'))}</td>
                <td className="right">−{inr(sum('salaryAdvance'))}</td>
                <td className="right">−{inr(sum('itemsTotal'))}</td>
                <td className="right">−{inr(sum('loanInstallment'))}</td>
                <td className="right">−{inr(sum('otherDeductions') - sum('bonus'))}</td>
                <td className="right"><strong>{inr(sum('netPay'))}</strong></td>
                <td className="right"><strong className="neg">{inr(sum('remaining'))}</strong></td>
                <td colSpan={2}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
      {adjust && <AdjustModal row={adjust} month={month} onClose={() => setAdjust(null)} onSaved={load} />}
      {payRow && <PayModal row={payRow} month={month} onClose={() => setPayRow(null)} onPaid={onPaid(payRow)} />}
      {viewSlip && <PayslipView slip={viewSlip} employee={viewSlip} onClose={() => setViewSlip(null)} />}
    </div>
  );
};

export default Payroll;
